import { useCallback } from "react";
import { Country } from "@lookiero/sty-psp-locale";
import { Segment } from "@lookiero/sty-psp-segment";
import { TrackingEventCategory, useEmitUserEvent } from "@lookiero/sty-psp-tracking";
import { Tradename } from "@lookiero/sty-sp-tradename";
import { Product } from "../../../domain/entities/product/Product";
import { financial } from "../shared/util/financial/financial";
import {
  Constants,
  PROJECT,
  TrackingEventName,
  WhishlistItemAddedTrackingEvent,
  WhishlistItemRemovedTrackingEvent,
} from "./tracking";

interface TrackWhishlistItemFunctionArgs {
  readonly product: Product;
  readonly list?: string;
}

interface TrackWhishlistItemFunction {
  (args: TrackWhishlistItemFunctionArgs): void;
}

interface UseTrackWhishlistItemFunctionArgs {
  readonly event: TrackingEventName.WISHLIST_ITEM_ADDED | TrackingEventName.WISHLIST_ITEM_REMOVED;
  readonly country: Country;
  readonly segment: Segment;
  readonly userId: string;
  readonly tradename: Tradename;
}

interface UseTrackWhishlistItemFunction {
  (agrs: UseTrackWhishlistItemFunctionArgs): TrackWhishlistItemFunction;
}

const useTrackWhishlistItem: UseTrackWhishlistItemFunction = ({ event, country, segment, tradename, userId }) => {
  const emitUserEvent = useEmitUserEvent<WhishlistItemAddedTrackingEvent | WhishlistItemRemovedTrackingEvent>();

  const trackWhishlistItem: TrackWhishlistItemFunction = useCallback(
    ({ product, list }) => {
      emitUserEvent({
        event,
        eventCategory: TrackingEventCategory.LOOK_IN,
        store: country,
        section: PROJECT,
        segment,
        tradename,
        userId,
        userUuid: userId,
        ecommerce: {
          items: [
            {
              item_name: product.title,
              item_id: product.variantId,
              price: financial(product.price.amount),
              item_brand: product.brand,
              item_category: Constants.NOT_SET,
              item_variant: product.size,
              list: list || Constants.COMPLETE_YOUR_LOOK_LIST,
              quantity: Constants.QUANTITY,
            },
          ],
        },
      });
    },
    [country, emitUserEvent, event, segment, tradename, userId],
  );

  return trackWhishlistItem;
};

export { useTrackWhishlistItem };
